import React, { useState } from 'react';
import './ReviewSection.css';
import { FaStar, FaQuoteLeft } from 'react-icons/fa';
import { BREVO_CONFIG } from '../config/email';

const initialReviews = [
  {
    id: 1,
    name: "J. Rochat",
    rating: 5,
    text: "Très satisfait du service. Le chauffeur était à l'heure pour mon transfert à l'aéroport de Genève, voiture impeccable. Je recommande !",
    date: "Mars 2024"
  },
  {
    id: 2,
    name: "M. Favre",
    rating: 5,
    text: "Utilise régulièrement pour mes déplacements professionnels. Service fiable et ponctuel. Les chauffeurs sont toujours courtois.",
    date: "Février 2024"
  },
  {
    id: 3,
    name: "F. Blanc",
    rating: 5,
    text: "Service parfait pour le transport scolaire de mes enfants. Le chauffeur est toujours le même, très rassurant. Merci !",
    date: "Janvier 2024"
  }
];

const ReviewSection = () => {
  const reviews = initialReviews;
  const [showForm, setShowForm] = useState(false);
  const [newReview, setNewReview] = useState({
    name: '',
    rating: 5,
    text: ''
  });
  const [hoveredStar, setHoveredStar] = useState(null);
  const [submitStatus, setSubmitStatus] = useState('');

  const renderStars = (rating, interactive = false) => {
    return [...Array(5)].map((_, index) => (
      <FaStar
        key={index}
        className={`taxivd-review-star ${
          interactive ? 'taxivd-review-star-interactive' : ''
        } ${(interactive ? (index < hoveredStar || (!hoveredStar && index < newReview.rating)) : index < rating)
          ? 'taxivd-review-star-filled'
          : ''
        }`}
        onMouseEnter={() => interactive && setHoveredStar(index + 1)}
        onMouseLeave={() => interactive && setHoveredStar(null)}
        onClick={() => interactive && setNewReview({ ...newReview, rating: index + 1 })}
      />
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');

    // Format the date in French
    const formattedDate = new Date().toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });

    // Create star rating display
    const stars = `${newReview.rating}/5 ★`;

    // Format email HTML content
    const emailHtml = `
      <html>
        <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
          <h2 style="color: #2c3e50;">Nouvel avis client</h2>
          
          <p><strong>Nom:</strong> ${newReview.name.trim()}</p>
          <p><strong>Note:</strong> ${stars}</p>
          <p><strong>Date:</strong> ${formattedDate}</p>
          
          <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px; margin-top: 20px;">Message:</h3>
          <p style="white-space: pre-wrap;">${newReview.text.replace(/\n/g, '<br>')}</p>
        </body>
      </html>
    `;

    // Format email text content (fallback)
    const emailText = `
Nouvel avis client

Nom: ${newReview.name.trim()}
Note: ${stars}
Date: ${formattedDate}

Message:
${newReview.text}
    `.trim();

    try {
      // Send email using Brevo API
      const response = await fetch(BREVO_CONFIG.API_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'api-key': BREVO_CONFIG.API_KEY
        },
        body: JSON.stringify({
          sender: {
            name: BREVO_CONFIG.FROM_NAME,
            email: BREVO_CONFIG.FROM_EMAIL
          },
          to: [
            {
              email: BREVO_CONFIG.RECIPIENT_EMAIL,
              name: 'TaxiVD'
            }
          ],
          subject: `Nouvel avis de ${newReview.name.trim()} - ${stars}`,
          htmlContent: emailHtml,
          textContent: emailText
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Review sent successfully:', result);
        setSubmitStatus('success');
        // Reset form
        setNewReview({ name: '', rating: 5, text: '' });
        setShowForm(false);
      } else {
        const errorData = await response.json();
        console.error('Failed to send review:', errorData);
        throw new Error(errorData.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Failed to send review:', error);
      setSubmitStatus('error');
      alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.\n\nErreur: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <section className="taxivd-review-section">
      <div className="taxivd-review-container">
        <div className="taxivd-review-header">
          <h2>Avis de nos Clients</h2>
          <p>Découvrez ce que nos clients disent de nos services</p>
          {!showForm && (
            <button 
              className="taxivd-review-add-button"
              onClick={() => setShowForm(true)}
            >
              Laisser un avis
            </button>
          )}
        </div>
        
        {showForm && (
          <form className="taxivd-review-form" onSubmit={handleSubmit}>
            <div className="taxivd-review-form-group">
              <label>Votre nom</label>
              <input
                type="text"
                value={newReview.name}
                onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                required
                placeholder="Entrez votre nom"
              />
            </div>
            
            <div className="taxivd-review-form-group">
              <label>Note</label>
              <div className="taxivd-review-rating-input">
                {renderStars(newReview.rating, true)}
              </div>
            </div>
            
            <div className="taxivd-review-form-group">
              <label>Votre avis</label>
              <textarea
                value={newReview.text}
                onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                required
                placeholder="Partagez votre expérience"
                rows="4"
              />
            </div>
            
            <div className="taxivd-review-form-buttons">
              <button 
                type="button" 
                className="taxivd-review-cancel-button"
                onClick={() => {
                  setShowForm(false);
                  setSubmitStatus('');
                }}
              >
                Annuler
              </button>
              <button 
                type="submit" 
                className="taxivd-review-submit-button"
                disabled={submitStatus === 'sending'}
              >
                {submitStatus === 'sending' ? 'Envoi en cours...' : 'Publier'}
              </button>
            </div>
            
            {submitStatus === 'error' && (
              <div className="taxivd-review-error">
                Une erreur est survenue lors de l'envoi. Veuillez réessayer.
              </div>
            )}
          </form>
        )}
        
        <div className="taxivd-reviews-grid">
          {reviews.map((review) => (
            <div key={review.id} className="taxivd-review-card">
              <div className="taxivd-review-quote-icon">
                <FaQuoteLeft />
              </div>
              <div className="taxivd-review-content">
                <div className="taxivd-review-rating">
                  {renderStars(review.rating)}
                </div>
                <p className="taxivd-review-text">{review.text}</p>
                <div className="taxivd-review-author">
                  <div className="taxivd-author-name">{review.name}</div>
                  <div className="taxivd-review-date">{review.date}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReviewSection; 